import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TabBar = ({ modules, activeId, onChange, dueCounts = {} }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [horizonFilter, setHorizonFilter] = useState('all');

  const horizonButtons = [
    { key: 'all', label: 'All Tabs' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'dueThisWeek', label: 'Due This Week' },
    { key: 'dueNextWeek', label: 'Due Next Week' },
    { key: 'dueThisMonth', label: 'Due This Month' },
    { key: 'dueNextMonth', label: 'Due Next Month' },
    { key: 'dueIn2Months', label: 'Due in 2 Months' },
    { key: 'dueIn3Months', label: 'Due in 3 Months' },
  ];

  const visibleModules = useMemo(() => {
    const allModules = Object.values(modules);
    if (horizonFilter === 'all') return allModules;

    return allModules.filter((mod) => {
      if (mod.id === activeId) return true;
      const metric = dueCounts?.[mod.id] || {};
      return Number(metric[horizonFilter] || 0) > 0;
    });
  }, [modules, horizonFilter, activeId, dueCounts]);

  const updateScrollControls = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollControls();
    window.addEventListener('resize', updateScrollControls);
    return () => window.removeEventListener('resize', updateScrollControls);
  }, [modules, visibleModules]);

  const scrollTabs = (delta) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({ left: delta, behavior: 'smooth' });
    setTimeout(updateScrollControls, 200);
  };

  return (
    <div className="w-full bg-slate-200 border-t border-slate-400 z-30 shadow-lg relative">
      <div className="px-3 pt-1 flex items-center justify-end">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar max-w-full pb-0.5">
          {horizonButtons.map((btn) => (
            <button
              key={btn.key}
              type="button"
              onClick={() => setHorizonFilter(btn.key)}
              className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-full transition-colors border whitespace-nowrap ${horizonFilter === btn.key ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'}`}
              title={`Filter tabs by ${btn.label.toLowerCase()}`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollTabs(-240)}
          className="absolute left-1 top-[68%] -translate-y-1/2 z-20 h-8 w-8 rounded-full border border-slate-300 bg-white/95 text-slate-700 hover:bg-white shadow-md"
          title="Scroll tabs left"
        >
          <ChevronLeft className="w-4 h-4 mx-auto" />
        </button>
      )}

      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollTabs(240)}
          className="absolute right-1 top-[68%] -translate-y-1/2 z-20 h-8 w-8 rounded-full border border-slate-300 bg-white/95 text-slate-700 hover:bg-white shadow-md"
          title="Scroll tabs right"
        >
          <ChevronRight className="w-4 h-4 mx-auto" />
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={updateScrollControls}
        className="overflow-x-auto no-scrollbar px-2 pt-1"
      >
        <div className="flex items-end gap-1 min-w-max px-2">
          {visibleModules.map((mod) => {
            const Icon = mod.icon;
            const isActive = activeId === mod.id;
            const metric = dueCounts?.[mod.id] || {};
            const overdue = Number(metric.overdue || 0);
            const dueThisWeek = Number(metric.dueThisWeek || 0);
            const dueNextWeek = Number(metric.dueNextWeek || 0);
            const dueThisMonth = Number(metric.dueThisMonth || 0);
            const dueNextMonth = Number(metric.dueNextMonth || 0);
            const dueIn2Months = Number(metric.dueIn2Months || 0);
            const dueIn3Months = Number(metric.dueIn3Months || 0);
            const activeFilterValue = horizonFilter !== 'all' ? Number(metric[horizonFilter] || 0) : 0;

            return (
              <button
                key={mod.id}
                onClick={() => onChange(mod.id)}
                title={mod.label}
                className={[
                  'flex items-center gap-2 px-3 py-2 rounded-t-2xl text-xs font-semibold',
                  'transition-all duration-200 border border-b-0 relative -mb-px whitespace-nowrap',
                  mod.colorClass,
                  'text-white',
                  isActive
                    ? 'opacity-100 scale-110 shadow-2xl ring-2 ring-white/60 border-slate-300 border-t-[3px] border-t-white/90 z-10'
                    : 'opacity-45 hover:opacity-75 border-transparent scale-100',
                ].join(' ')}
              >
                {isActive && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-full bg-white/80" />
                )}

                <div className="relative">
                  <Icon className="w-3.5 h-3.5 text-white transition-colors" />
                </div>
                <span className="tracking-wide">{mod.label}</span>

                {horizonFilter !== 'all' ? (
                  activeFilterValue > 0 && (
                    <div className="ml-1">
                      <span className="inline-flex items-center rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none border border-white/30">
                        {activeFilterValue > 99 ? '99+' : activeFilterValue}
                      </span>
                    </div>
                  )
                ) : (overdue > 0 || dueThisWeek > 0 || dueNextWeek > 0 || dueThisMonth > 0 || dueNextMonth > 0 || dueIn2Months > 0 || dueIn3Months > 0) && (
                  <div className="flex items-center gap-1 ml-1">
                    {overdue > 0 && (
                      <span className="inline-flex items-center rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        O {overdue > 99 ? '99+' : overdue}
                      </span>
                    )}
                    {dueThisWeek > 0 && (
                      <span className="inline-flex items-center rounded-full bg-amber-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        W {dueThisWeek > 99 ? '99+' : dueThisWeek}
                      </span>
                    )}
                    {dueNextWeek > 0 && (
                      <span className="inline-flex items-center rounded-full bg-orange-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        NW {dueNextWeek > 99 ? '99+' : dueNextWeek}
                      </span>
                    )}
                    {dueThisMonth > 0 && (
                      <span className="inline-flex items-center rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        M {dueThisMonth > 99 ? '99+' : dueThisMonth}
                      </span>
                    )}
                    {dueNextMonth > 0 && (
                      <span className="inline-flex items-center rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        NM {dueNextMonth > 99 ? '99+' : dueNextMonth}
                      </span>
                    )}
                    {dueIn2Months > 0 && (
                      <span className="inline-flex items-center rounded-full bg-cyan-600 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        M+2 {dueIn2Months > 99 ? '99+' : dueIn2Months}
                      </span>
                    )}
                    {dueIn3Months > 0 && (
                      <span className="inline-flex items-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                        M+3 {dueIn3Months > 99 ? '99+' : dueIn3Months}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
export default TabBar;